import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useNotification } from '@dynamon-as/ui';
import { useRequestStore, selectError } from '@features/requests';
import { toApiAvailabilityPattern } from '@features/requests/utils';
import { useEmployeeId } from '@shared/hooks';
import { useOrgEmployeeRequestsApi } from '@shared/services';
import type { CreateRequestDto, Conflict, Request } from '@features/requests';
import { RequestForm, ConflictWarning } from '@features/requests/components';
import { NOTIFICATION_MESSAGES } from '@features/requests/constants';
import { useOrgEmployeeStore, selectOrgEmployee } from '@store/orgEmployee';
import styles from './RequestNew.module.css';

/**
 * Request New Page
 * Page for creating a new request
 */
export default function RequestNew(): React.ReactElement {
    // Router navigation
    const navigate = useNavigate();

    // Get store data and actions
    const error = useRequestStore(selectError);
    const createRequest = useRequestStore(
        (state): ((dto: CreateRequestDto, employeeId: string) => Request) =>
            state.createRequest,
    );
    const submitRequest = useRequestStore(
        (
            state,
        ): ((
            id: string,
            employeeId: string,
        ) => Promise<
            | { success: true; request: Request; conflicts?: Conflict[] }
            | { success: false; error: string; conflicts?: Conflict[] }
        >) => state.submitRequest,
    );
    const clearError = useRequestStore(
        (state): (() => void) => state.clearError,
    );

    // Use Notification from @dynamon-as/ui
    const { notify } = useNotification();

    // Get employeeId from centralized hook
    const { employeeId, orgEmployeeId, error: employeeError } = useEmployeeId();
    const [showConflictWarning, setShowConflictWarning] = useState(false);
    const [conflictsToShow, setConflictsToShow] = useState<Conflict[]>([]);
    const [createdRequestId, setCreatedRequestId] = useState<string | null>(
        null,
    );

    const orgEmployee = useOrgEmployeeStore(selectOrgEmployee);

    // Get orgEmployeeId from store if not from hook
    const effectiveOrgEmployeeId = orgEmployeeId ?? orgEmployee?.id;
    const { update: updateRequest, addComment: addCommentApi } =
        useOrgEmployeeRequestsApi();

    // Show employee error notifications
    useEffect((): void => {
        if (employeeError) {
            notify.error(employeeError);
        }
    }, [employeeError, notify]);

    // Show error notifications
    useEffect((): void => {
        if (error) {
            notify.error(error);
            clearError();
        }
    }, [error, notify, clearError]);

    /**
     * Sends notes as a comment to the request
     */
    const sendNotesAsComment = useCallback(
        async (requestId: string, notesText: string): Promise<boolean> => {
            if (!effectiveOrgEmployeeId || !notesText) {
                return false;
            }

            try {
                const orgId: string = effectiveOrgEmployeeId;
                await addCommentApi(orgId, requestId, notesText);
                return true;
            } catch {
                return false;
            }
        },
        [addCommentApi, effectiveOrgEmployeeId],
    );

    /**
     * Saves request to backend as draft
     */
    const saveRequestAsDraft = useCallback(
        async (
            request: Request,
            notesText: string | null,
        ): Promise<boolean> => {
            if (!effectiveOrgEmployeeId) {
                notify.success(NOTIFICATION_MESSAGES.REQUEST_SAVED_AS_DRAFT);
                return true;
            }

            try {
                const pattern = toApiAvailabilityPattern(request);
                await updateRequest(
                    effectiveOrgEmployeeId,
                    request.id,
                    pattern,
                );

                // Only send comment if request was saved successfully
                if (notesText) {
                    const commentAdded = await sendNotesAsComment(
                        request.id,
                        notesText,
                    );
                    if (commentAdded) {
                        notify.success(
                            'Søknad lagret som utkast og kommentar lagt til',
                        );
                    } else {
                        notify.success(
                            NOTIFICATION_MESSAGES.REQUEST_SAVED_AS_DRAFT,
                        );
                    }
                } else {
                    notify.success(
                        NOTIFICATION_MESSAGES.REQUEST_SAVED_AS_DRAFT,
                    );
                }
                return true;
            } catch {
                notify.error('Feil ved lagring av utkast');
                return false;
            }
        },
        [updateRequest, effectiveOrgEmployeeId, notify, sendNotesAsComment],
    );

    /**
     * Handles form submission for creating a new request
     */
    const handleCreateSubmit = async (
        dto: CreateRequestDto,
        isSubmit: boolean = false,
    ): Promise<void> => {
        if (!employeeId) {
            notify.error('Employee ID not available');
            return;
        }

        try {
            const notesText = dto.notes && dto.notes.trim() ? dto.notes : null;
            const createDto: CreateRequestDto = { ...dto, notes: '' };
            const createdRequest = await createRequest(createDto, employeeId);
            setCreatedRequestId(createdRequest.id);

            if (isSubmit) {
                // First submit the request
                const result = await submitRequest(
                    createdRequest.id,
                    employeeId,
                );

                if (result.success) {
                    // Only send comment if submit was successful
                    if (notesText) {
                        await sendNotesAsComment(createdRequest.id, notesText);
                    }

                    if (result.conflicts) {
                        setConflictsToShow(result.conflicts);
                        setShowConflictWarning(true);
                    } else {
                        notify.success(NOTIFICATION_MESSAGES.REQUEST_SENT);
                        navigate('/requests');
                    }
                } else if (result.conflicts) {
                    const errorMessages = result.conflicts
                        .map((c: Conflict): string => c.message)
                        .join(' ');
                    notify.error(errorMessages);
                } else {
                    notify.error('No se pudo enviar la solicitud');
                }
            } else {
                const draftSaved = await saveRequestAsDraft(
                    createdRequest,
                    notesText,
                );
                if (draftSaved) {
                    navigate('/requests');
                }
            }
        } catch {
            notify.error('No se pudo crear la solicitud');
        }
    };

    /**
     * Confirms submission with warnings
     */
    const handleConfirmWithWarnings = useCallback(async (): Promise<void> => {
        if (createdRequestId && employeeId) {
            // Submit the created request through the store
            const result = await submitRequest(createdRequestId, employeeId);
            if (result.success) {
                notify.success(NOTIFICATION_MESSAGES.REQUEST_SENT);
            } else {
                notify.error('No se pudo enviar la solicitud');
            }
        }
        setShowConflictWarning(false);
        setConflictsToShow([]);
        navigate('/requests');
    }, [createdRequestId, employeeId, notify, navigate, submitRequest]);

    /**
     * Cancels submission with warnings
     */
    const handleCancelWithWarnings = (): void => {
        setShowConflictWarning(false);
        setConflictsToShow([]);
    };

    /**
     * Handles form cancel - go back to list
     */
    const handleFormCancel = (): void => {
        navigate('/requests');
    };

    return (
        <div className={styles.container}>
            {/* Show Conflict Warning Modal */}
            {showConflictWarning && (
                <ConflictWarning
                    conflicts={conflictsToShow}
                    onConfirm={(): void => void handleConfirmWithWarnings()}
                    onCancel={handleCancelWithWarnings}
                    open={showConflictWarning}
                />
            )}

            {/* Show Request Form */}
            <RequestForm
                onSubmit={(dto): void =>
                    void handleCreateSubmit(dto as CreateRequestDto, false)
                }
                onCancel={handleFormCancel}
                isCreate={true}
                onSubmitAction={(dto): void =>
                    void handleCreateSubmit(dto as CreateRequestDto, true)
                }
                initialExcludedDates={undefined}
            />
        </div>
    );
}
